import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Building, Mail, Phone, User, Globe, MapPin, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CompanyInfo, NotificationPreferences } from '@/contexts/auth/types';
import { UserRoles } from '@/contexts/auth/enums';
import TestNotificationButton from './TestNotificationButton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

interface ProfileSidebarProps {
  name: string;
  email?: string;
  contactPhone?: string;
  website?: string;
  role?: string;
  notificationPreferences?: NotificationPreferences;
  companyName?: string;
  companyInfo?: CompanyInfo;
  compact?: boolean;
}

const ProfileSidebar = ({
  name,
  email,
  contactPhone,
  website,
  role,
  notificationPreferences,
  companyName,
  companyInfo,
  compact = false
}: ProfileSidebarProps) => {
  const navigate = useNavigate();

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleNotificationSettingsClick = () => {
    // Navigate to the notifications tab in the profile page
    if (role === UserRoles.ORGANIZER) {
      navigate('/organizer/profile', { state: { defaultTab: 'notifications', _ts: Date.now() } });
    } else {
      navigate('/participant/profile', { state: { defaultTab: 'ertesitesek', _ts: Date.now() } });
    }
  };

  // Format the address display
  const formatAddress = (address?: CompanyInfo['address']) => {
    if (!address) return null;

    const parts = [];
    if (address.postalCode) parts.push(address.postalCode);
    if (address.city) parts.push(address.city);
    if (address.street) parts.push(address.street);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  // Compact version for dropdown
  if (compact) {
    return (
      <div className="py-2">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-10 w-10 bg-slate-200 text-slate-600">
            <AvatarFallback>{getInitials(name)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="font-medium">{name}</span>
            {role && <span className="text-xs text-muted-foreground">{role}</span>}
          </div>
        </div>

        <Separator className="my-2" />

        <div className="space-y-2 px-2">
          {email && (
            <div className="flex items-center">
              <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm truncate">{email}</span>
            </div>
          )}

          {contactPhone && (
            <div className="flex items-center">
              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">{contactPhone}</span>
            </div>
          )}

          {companyName && (
            <div className="flex items-center">
              <Building className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm truncate">{companyName}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full sidebar version
  return (
    <Card>
      <CardContent className="p-6 flex flex-col justify-center items-center">
        <div className="flex flex-col items-center text-center mb-6">
          <Avatar className="h-24 w-24 text-2xl bg-slate-200 text-slate-600 mb-4">
            <AvatarFallback>{getInitials(name)}</AvatarFallback>
          </Avatar>
          <h3 className="font-semibold text-lg">{name}</h3>
          {role && <span className="text-sm text-muted-foreground">{role}</span>}
        </div>

        <Separator className="my-4" />

        <div className="space-y-3">
          {email && (
            <div className="flex items-center">
              <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm truncate">{email}</span>
            </div>
          )}

          {contactPhone && (
            <div className="flex items-center">
              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">{contactPhone}</span>
            </div>
          )}

          {website && (
            <div className="flex items-center">
              <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm truncate">{website}</span>
            </div>
          )}

          {companyName && (
            <div className="flex items-center">
              <Building className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">{companyName}</span>
            </div>
          )}
        </div>

        {notificationPreferences && (
          <>
            <Separator className="my-4" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Bell className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-sm">Értesítések</span>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${notificationPreferences.enabled
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                    }`}
                >
                  {notificationPreferences.enabled ? 'Bekapcsolva' : 'Kikapcsolva'}
                </span>
              </div>

              {notificationPreferences.enabled && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Értesítési mód:</span>
                    <span>{notificationPreferences.notificationMethod}</span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Gyakoriság:</span>
                    <span>{notificationPreferences.frequency}</span>
                  </div>

                  {/*                   <TestNotificationButton className="w-full mt-2" />
 */}                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleNotificationSettingsClick}
              >
                <Bell className="mr-2 h-4 w-4" />
                Értesítési beállítások
              </Button>
            </div>
          </>
        )}

        {companyInfo && companyInfo.taxNumber && (
          <>
            <Separator className="my-4" />

            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Building className="h-4 w-4" />
                Céges adatok
              </h4>
              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">Cégnév:</span> {companyInfo.name}</div>
                <div><span className="text-muted-foreground">Adószám:</span> {companyInfo.taxNumber}</div>
                {companyInfo.registrationNumber && (
                  <div><span className="text-muted-foreground">Cégjegyzékszám:</span> {companyInfo.registrationNumber}</div>
                )}
                {companyInfo.address && formatAddress(companyInfo.address) && (
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="flex-1">{formatAddress(companyInfo.address)}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileSidebar;
